// frontend/src/components/p2p/OrderFlowTerminal.tsx
import { useEffect, useMemo, useRef, useState } from 'react'
import { X, Copy, ChevronDown, ChevronRight } from 'lucide-react'
import type { CreateOrderStage, FlowStep, FlowStepLevel } from '../../hooks/useCreateOrder'
import { cn } from '../../lib/utils'

interface Props {
  open: boolean
  stage: CreateOrderStage
  steps: FlowStep[]
  txHash: `0x${string}` | null
  error: string | null
  onClose: () => void
  /** Optional human-friendly title shown in the header bar. */
  title?: string
}

const IN_FLIGHT: CreateOrderStage[] = [
  'building',
  'switching',
  'checkingAllowance',
  'approving',
  'creating',
  'matching',
]

/**
 * Terminal-style modal that mirrors every step the useCreateOrder hook
 * goes through (build calldata → switch chain → allowance → approve →
 * tx → backend matching). It keeps the modal open while in-flight,
 * shows errors expanded, lets the user copy the full log.
 *
 * Designed to be reused by both the "Create order" flow (OrderForm) and
 * the "Tomar" flow (OrderBookTabs) — the same hook backs both, so the
 * same component renders both.
 */
export function OrderFlowTerminal({
  open,
  stage,
  steps,
  txHash,
  error,
  onClose,
  title = 'Flujo de la orden',
}: Props) {
  const inFlight = IN_FLIGHT.includes(stage)
  const scrollRef = useRef<HTMLDivElement | null>(null)

  // Auto-scroll the log to bottom whenever a new step lands.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [steps.length])

  const logText = useMemo(() => buildPlainLog(steps, txHash, error), [steps, txHash, error])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      // Block click-through-to-close while in-flight — the user might
      // accidentally dismiss the wallet's signing pop-up tracker.
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !inFlight) onClose()
      }}
    >
      <div className="w-full max-w-2xl rounded-lg border border-dark-700 bg-dark-900 shadow-2xl flex flex-col max-h-[80vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-dark-800">
          <div className="flex items-center gap-2">
            <div
              className={cn(
                'h-2 w-2 rounded-full',
                inFlight
                  ? 'bg-amber-400 animate-pulse'
                  : stage === 'success'
                  ? 'bg-emerald-400'
                  : stage === 'error'
                  ? 'bg-rose-400'
                  : 'bg-dark-500',
              )}
            />
            <span className="text-sm font-medium text-dark-100">{title}</span>
            <span className="text-[11px] font-mono uppercase text-dark-500">
              {stage}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => copyLog(logText)}
              className="text-xs text-dark-400 hover:text-white px-2 py-1 rounded hover:bg-dark-800 flex items-center gap-1"
              title="Copiar log al portapapeles"
            >
              <Copy className="h-3 w-3" /> Copiar
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={inFlight}
              className={cn(
                'text-dark-400 hover:text-white p-1 rounded hover:bg-dark-800',
                inFlight && 'opacity-30 cursor-not-allowed hover:bg-transparent',
              )}
              title={inFlight ? 'Espera a que termine el flujo' : 'Cerrar'}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Log */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-4 py-3 font-mono text-[12px] leading-relaxed bg-dark-950/60"
        >
          {steps.length === 0 ? (
            <div className="text-dark-500">Iniciando…</div>
          ) : (
            steps.map((s, i) => <StepLine key={i} step={s} />)
          )}
          {error && (
            <ErrorBlock message={error} detail={lastErrorDetail(steps)} />
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-dark-800 flex items-center justify-between text-[11px] text-dark-500">
          {txHash ? (
            <span className="font-mono">
              tx: <span className="text-dark-300">{txHash.slice(0, 12)}…{txHash.slice(-8)}</span>
            </span>
          ) : (
            <span>—</span>
          )}
          <span>
            {inFlight
              ? 'No cierres esta ventana mientras esté en curso'
              : stage === 'success'
              ? '✓ Flujo completado'
              : stage === 'error'
              ? '✗ Flujo abortado'
              : ''}
          </span>
        </div>
      </div>
    </div>
  )
}

// ----------------------------------------------------------------------------

function StepLine({ step }: { step: FlowStep }) {
  const [expanded, setExpanded] = useState(false)
  const hasDetail = Boolean(step.detail && step.detail.length > 0)
  const ts = formatTs(step.at)
  const color = COLOR[step.level]
  const prefix = PREFIX[step.level]
  return (
    <div className={cn('flex gap-2 items-start py-0.5', color)}>
      <span className="text-dark-500 shrink-0">[{ts}]</span>
      <span className="shrink-0">{prefix}</span>
      <div className="flex-1 min-w-0">
        <div className="break-words flex items-start gap-1">
          {hasDetail && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="shrink-0 text-dark-500 hover:text-dark-200 mt-0.5"
            >
              {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            </button>
          )}
          <span>{step.message}</span>
        </div>
        {hasDetail && expanded && (
          <pre className="mt-1 ml-4 p-2 rounded bg-dark-900 border border-dark-800 text-[11px] text-dark-300 whitespace-pre-wrap break-all">
            {step.detail}
          </pre>
        )}
      </div>
    </div>
  )
}

function ErrorBlock({ message, detail }: { message: string; detail?: string }) {
  return (
    <div className="mt-3 rounded border border-rose-700/40 bg-rose-950/30 p-3 text-[12px]">
      <div className="text-rose-300 font-medium mb-1">Error</div>
      <div className="text-rose-200">{message}</div>
      {detail && (
        <pre className="mt-2 p-2 rounded bg-dark-950 border border-rose-900/30 text-[11px] text-rose-300/80 whitespace-pre-wrap break-all">
          {detail}
        </pre>
      )}
    </div>
  )
}

const COLOR: Record<FlowStepLevel, string> = {
  info: 'text-dark-200',
  progress: 'text-amber-200',
  success: 'text-emerald-300',
  warn: 'text-amber-400',
  error: 'text-rose-300',
}

const PREFIX: Record<FlowStepLevel, string> = {
  info: '▸',
  progress: '…',
  success: '✓',
  warn: '!',
  error: '✗',
}

function formatTs(epochMs: number): string {
  const d = new Date(epochMs)
  return d.toTimeString().slice(0, 8)
}

function buildPlainLog(steps: FlowStep[], txHash: string | null, error: string | null): string {
  const lines = steps.map(
    (s) =>
      `[${formatTs(s.at)}] ${PREFIX[s.level]} ${s.message}${s.detail ? `\n    ${s.detail.replace(/\n/g, '\n    ')}` : ''}`,
  )
  if (txHash) lines.push(`tx: ${txHash}`)
  if (error) lines.push(`ERROR: ${error}`)
  return lines.join('\n')
}

function lastErrorDetail(steps: FlowStep[]): string | undefined {
  for (let i = steps.length - 1; i >= 0; i--) {
    if (steps[i].level === 'error' && steps[i].detail) return steps[i].detail
  }
  return undefined
}

function copyLog(text: string) {
  if (navigator.clipboard) {
    navigator.clipboard.writeText(text).catch(() => {})
  }
}
